import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Handshake, DollarSign, Users, BarChart3, ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const benefits = [
  { icon: DollarSign, title: "20% recurring commission", desc: "Earn on every client you refer — for as long as they're subscribed." },
  { icon: Users, title: "Dedicated partner dashboard", desc: "Track referrals, commissions, and payouts in real time." },
  { icon: BarChart3, title: "Co-branded materials", desc: "Get landing pages, one-pagers, and email templates to share with clients." },
];

const partnerTypes = [
  { label: "Commercial Real Estate Broker", desc: "Help your tenants open faster with a clear roadmap." },
  { label: "Business Attorney", desc: "Recommend a tool that handles the planning before they need legal." },
  { label: "Restaurant Consultant", desc: "Augment your services with AI-powered planning." },
  { label: "Accountant / CPA", desc: "Give clients realistic cost projections before they overspend." },
  { label: "SBA Lender / Bank", desc: "Assess borrower readiness with investor-grade plans." },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.5 },
});

export default function Affiliates() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) return;
    setSubmitted(true);
    toast.success("Application received! We'll be in touch within 48 hours.");
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center justify-between">
          <span className="font-heading text-xl font-medium tracking-tight text-foreground cursor-pointer" onClick={() => navigate("/")}>
            oplif<span className="text-primary">.</span>
          </span>
          <Button size="sm" variant="ghost" onClick={() => navigate(-1)} className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
        </div>
      </div>

      <div className="container max-w-4xl pt-16">
        {/* Hero */}
        <motion.div {...fade()} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/8 border border-primary/15 text-xs font-medium text-primary mb-6">
            <Handshake className="w-3.5 h-3.5" /> Partner Program
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium text-foreground mb-4">
            Earn by helping founders open smarter
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Join Oplif's affiliate program for brokers, attorneys, consultants, and anyone who works with aspiring restaurant and café founders.
          </p>
        </motion.div>

        {/* Benefits */}
        <div className="grid sm:grid-cols-3 gap-6 mb-20">
          {benefits.map((b, i) => (
            <motion.div key={b.title} {...fade(0.1 * i)} className="bg-card rounded-xl p-6 border border-border">
              <b.icon className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-heading font-medium text-foreground mb-2">{b.title}</h3>
              <p className="text-sm text-muted-foreground">{b.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Who it's for */}
        <motion.div {...fade(0.15)} className="mb-20">
          <h2 className="font-heading text-2xl font-medium text-foreground mb-8 text-center">Who is this for?</h2>
          <div className="space-y-3">
            {partnerTypes.map(p => (
              <div key={p.label} className="flex items-start gap-3 bg-card rounded-xl p-4 border border-border">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-foreground text-sm">{p.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Apply form */}
        <motion.div {...fade(0.2)} className="bg-card rounded-2xl border border-border p-8 sm:p-12 max-w-lg mx-auto">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-medium text-foreground mb-2">Application received!</h3>
              <p className="text-muted-foreground">We'll review your application and get back to you within 48 hours.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-6">
                <Mail className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg font-medium text-foreground">Apply to become a partner</h3>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} required />
                <Input placeholder="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                <Input placeholder="Company (optional)" value={company} onChange={e => setCompany(e.target.value)} />
                <Button type="submit" className="w-full gap-2">
                  Submit application <ArrowRight className="w-4 h-4" />
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
